// src/components/CatylexSearch/CatylexDocumentCard.tsx

import * as React from "react";
import { useCallback } from "react";
import { NormalizedCatylexSearchResult } from "../../interfaces/catylexSearchTypes";
import { percentLoaded } from "../../utils/documentSize";
import { DocumentIcon, ExternalLinkIcon } from "./CatylexIcons";
import { catylexColors } from "./catylexSearchStyles";
import { cardStyle, fieldGridStyle, fieldLabelStyle, fieldValueStyle } from "./catylexCardStyles";

interface CatylexDocumentCardProps {
  result: NormalizedCatylexSearchResult;
}

/** Quiet caveat below the metadata — the one thing that limits what the text can answer. */
const noteStyle: React.CSSProperties = {
  margin: "8px 0 0",
  padding: "6px 10px",
  borderLeft: `3px solid ${catylexColors.quoteBorder}`,
  backgroundColor: catylexColors.quoteBg,
  borderRadius: "0 4px 4px 0",
  fontSize: "11px",
  lineHeight: "1.5",
  color: catylexColors.headerText,
};

/**
 * The exact counts behind the coverage row: "1,000 of 1,500 sentences" when some
 * are missing, "620 sentences" when none are, "" when a number never arrived.
 */
const describeCounts = (
  returned: number | undefined,
  total: number | undefined,
  unit: string
): string => {
  if (returned == null || total == null) return "";
  return returned < total
    ? `${returned.toLocaleString()} of ${total.toLocaleString()} ${unit}`
    : `${total.toLocaleString()} ${unit}`;
};

/**
 * The same counts as a parenthetical for the truncation note, or "" when nothing
 * true can be said. Prints only when both numbers arrived AND actually differ —
 * a `??` fallback between the two pairs is what produced the self-refuting
 * "(120,000 of 120,000 characters)".
 */
const describeShortfall = (
  returned: number | undefined,
  total: number | undefined,
  unit: string
): string =>
  returned != null && total != null && returned < total
    ? ` (${describeCounts(returned, total, unit)})`
    : "";

const CatylexDocumentCard: React.FC<CatylexDocumentCardProps> = ({ result }) => {
  const reportUrl = result.allTermsReportUrl;

  const handleOpenReport = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (reportUrl && /^https?:\/\//i.test(reportUrl)) {
        window.open(reportUrl, "_blank", "noopener,noreferrer");
      }
    },
    [reportUrl]
  );

  const title = result.displayTitle || result.fileName || result.contractId || "Contract";
  const showFileName = !!result.fileName && result.fileName !== title;

  // WCAG 2.5.3 (Label in Name): the visible text must appear in the accessible
  // name, so the name leads with it verbatim and only then disambiguates which
  // contract. `title` reuses the same string — a tooltip and an announced name
  // that disagree are two answers to one question. The destination is the
  // contract's own record, not a report about it, which is why this reads
  // differently from the search cards' terms-report link.
  const openLabel = `View in Catylex: ${title} (opens in a new window)`;

  // Sentences are the only unit here, because the backend defines truncation as
  // `sentences_returned < total_sentences` — that pair IS the flag. It used to
  // be shadowed by a character pair, which could not see the one truncation
  // case that actually happens: the report's per-request sentence window. A
  // character ratio came back a flat 100% there, since it only ever summed the
  // window it fetched rather than the whole document.
  const truncationCounts = describeShortfall(
    result.sentencesReturned,
    result.totalSentences,
    "sentences"
  );

  // This row used to print an estimated page count, which was wrong on sight:
  // estimatePages is a chars-per-page heuristic with no relation to the
  // document's real pagination, so "~13 pages" sat next to a PDF the reader
  // could see was 40. Coverage is the question they actually have — is all of
  // this contract in context, or only part of it — and the backend answers it
  // with counts it genuinely has.
  const loadedPercent = percentLoaded(result.sentencesReturned, result.totalSentences);
  const partialPercent = loadedPercent != null && loadedPercent < 100 ? loadedPercent : null;

  // `truncated` is the backend's verdict and the counts are its evidence. Where
  // they disagree, believe whichever says something is missing: a row reading
  // "Entire contract" directly above "Only part of this contract is loaded" is
  // the one combination that reads as a bug.
  const isPartial = result.truncated === true || partialPercent != null;

  const fieldRows: Array<{ key: string; label: string; value: string; hint?: string }> = [];

  if (showFileName) {
    fieldRows.push({
      key: "file",
      label: "File",
      value: result.fileName!,
      hint: result.fileName!,
    });
  }

  fieldRows.push({
    key: "loaded",
    label: "Loaded",
    // "Part of the contract" is the honest floor: the backend flagged a cut but
    // sent no pair of counts we can turn into a share of the whole.
    value: !isPartial
      ? "Entire contract"
      : partialPercent != null
        ? `${partialPercent}% of the contract`
        : "Part of the contract",
    // The exact counts stay one hover away for anyone who wants to check the
    // percentage — same pair the percentage was computed from.
    hint: describeCounts(result.sentencesReturned, result.totalSentences, "sentences") || undefined,
  });

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <DocumentIcon stroke={catylexColors.primary} />
        <p
          style={{
            flex: 1,
            minWidth: 0,
            margin: 0,
            fontSize: "13px",
            fontWeight: 600,
            color: catylexColors.foreground,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={title}
        >
          {title}
        </p>
        <span
          style={{
            fontSize: "10px",
            fontWeight: 500,
            padding: "2px 6px",
            borderRadius: "4px",
            backgroundColor: catylexColors.primaryBg,
            color: catylexColors.primary,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          In context
        </span>
      </div>

      {/* Says what the reader can now do, not where the text went — the header
          two lines up already named the contract. "full" is dropped when the
          document was truncated so this can't contradict the note below. */}
      <p
        style={{
          margin: "8px 0 0",
          fontSize: "12px",
          lineHeight: "1.5",
          color: catylexColors.headerText,
        }}
      >
        {result.truncated ? "The text" : "The full text"} is now available — ask about it, or
        compare it with the open document.
      </p>

      <div style={fieldGridStyle}>
        {fieldRows.map((row) => (
          <React.Fragment key={row.key}>
            <span style={fieldLabelStyle}>{row.label}</span>
            <span style={fieldValueStyle} title={row.hint}>
              {row.value}
            </span>
          </React.Fragment>
        ))}
      </div>

      {/* Reachable in v0: the all-terms report returns at most 1000 sentences per
          request, so a longer contract is windowed rather than refused. */}
      {result.truncated && (
        <p style={noteStyle}>
          Only part of this contract is loaded{truncationCounts}. To check anything that isn&apos;t
          here, ask to search within this contract — re-loading it returns the same opening
          sentences.
        </p>
      )}

      {reportUrl && (
        <button
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginTop: "10px",
            padding: "4px 0",
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "11px",
            fontWeight: 500,
            fontFamily: "inherit",
            color: catylexColors.primary,
          }}
          onClick={handleOpenReport}
          aria-label={openLabel}
          title={openLabel}
        >
          View in Catylex
          <ExternalLinkIcon />
        </button>
      )}
    </div>
  );
};

export default CatylexDocumentCard;
